import { useContext, useLayoutEffect } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import { setStyleVar } from '../utils/style';
import { SatCoreRegister } from '../SatCoreRegistry';

/**
 * Support tracking various side effects for caution banners, including style.
 *
 * Example: track the total height of active caution banners and expose it via
 * --caution-banner-active-total-height so downstream layout can reference it
 * (e.g. subtract it from a container's height to avoid double scrollbars).
 *
 * @example
 * .class-detail.vertical-course-toc-active {
 *   height: calc(100vh / var(--main-view-zoom, 1) - var(--nav-height) - var(--caution-banner-active-total-height, 0px));
 * }
 */
const CautionBannerObserver = observer(({ hasCautionBanner }) => {
  const { notificationManager, userManager } = useContext(MobXProviderContext);
  const { isImpersonatorAdmin } = userManager;
  const { isCautionBannerLoading, isCautionBannerMinimized } = notificationManager;

  useLayoutEffect(() => {
    const cautionBannerActiveTotalHeight = '--caution-banner-active-total-height';

    // No banners in this render — reset the var so downstream height calcs collapse the offset.
    if (!hasCautionBanner) {
      setStyleVar(cautionBannerActiveTotalHeight, '0px');
      return;
    }

    // Track the last integer-pixel total we pushed into --caution-banner-active-total-height so we
    // can skip redundant writes on subsequent measurements.
    let lastWrittenTotal = null;
    const measure = () => {
      const bannerElements = document.querySelectorAll('.caution-banner-inner');
      // Skip the write when no banner is currently in the DOM. CautionBanner briefly unmounts +
      // remounts on collapse/expand; writing 0 here would flicker .class-detail's height for a frame.
      if (bannerElements.length === 0) {
        return;
      }
      // Round to integer px so sub-pixel jitter cannot re-trigger writes.
      const total = Math.round([...bannerElements].reduce(
        (sum, bannerElement) => sum + bannerElement.getBoundingClientRect().height, 0
      ));
      // Skip the write when this measurement equals the last one we wrote. Otherwise collapse/expand
      // can self-sustain — our var write shifts layout, scrollbar toggles, viewport width changes,
      // banner text re-wraps, banner height changes, observer fires again.
      if (total === lastWrittenTotal) {
        return;
      }
      lastWrittenTotal = total;
      setStyleVar(cautionBannerActiveTotalHeight, `${total}px`);
    };

    // Catch minimize/maximize + text-wrap height changes.
    // requestAnimationFrame defers measure() to the next frame — otherwise measure() writing to
    // --caution-banner-active-total-height can trigger another resize on the same frame, which
    // browsers flag as a ResizeObserver loop.
    const resizeObserver = new ResizeObserver(() => requestAnimationFrame(measure));
    document.querySelectorAll('.caution-banner-inner').forEach(
      (bannerElement) => resizeObserver.observe(bannerElement)
    );
    measure();

    return () => {
      resizeObserver.disconnect();
      // Do not reset the var here — this cleanup also runs during CautionBanner's transient
      // unmount/remount on collapse/expand, and resetting would flicker .class-detail height. The
      // !hasCautionBanner branch above handles the true "no more banners" case.
    };
    // Depend on isCautionBannerLoading + isCautionBannerMinimized so the effect re-runs after the
    // banner unmount/remount cycle that CautionBanner triggers on collapse/expand — otherwise our
    // ResizeObserver stays attached to the old (removed) element and never sees the new one.
  }, [hasCautionBanner, isCautionBannerLoading, isCautionBannerMinimized, isImpersonatorAdmin]);

  return null;
});

export default CautionBannerObserver;
SatCoreRegister('CautionBannerObserver', CautionBannerObserver);
